import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Users, HeartPulse, Compass } from "lucide-react";
import usePageMeta from "../../lib/usePageMeta";

export default function AboutPage() {
  usePageMeta({
    title: "About CCCHAPS | California Corporate Chaplains",
    description: "Workforce chaplaincy built for California's executive, HR, and people leaders. Trusted, non-clinical emotional and workplace support — relational rather than transactional, and always voluntary.",
  });
  return (
    <div data-testid="about-page">
      <section className="bg-gradient-to-b from-[#F8FAFC] to-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 md:pt-20 pb-12">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            <div className="lg:col-span-7">
              <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-3">About CCCHAPS</p>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight text-slate-900 leading-[1.05] max-w-3xl">
                Workforce chaplaincy built for California's executive, HR, and people leaders.
              </h1>
              <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-2xl">
                California Corporate Chaplains is a workplace chaplaincy and workforce support organization headquartered in California. CCCHAPS currently provides scheduled onsite chaplaincy throughout Sacramento, Placer, El Dorado, and Nevada Counties, with remote chaplain support available across California and nationwide, based on service arrangement and availability. Our chaplains provide trusted, non-clinical emotional and workplace support — relational rather than transactional, and always voluntary.
              </p>
            </div>
            <div className="lg:col-span-5">
              <div
                className="aspect-[4/3] rounded-2xl overflow-hidden shadow-sm border border-slate-200 bg-slate-100"
                data-testid="about-hero-image"
              >
                <img
                  src="/images/about/hero.jpg"
                  alt="A diverse group of professionals collaborating around a table in a bright, modern workplace — introductions and a warm handshake between colleagues."
                  loading="eager"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            { icon: Users, title: "Workforce support", body: "Designed for California's modern, distributed, and hybrid teams." },
            { icon: ShieldCheck, title: "Confidentiality", body: "Chaplain conversations are handled confidentially within appropriate professional, ethical, safety, and legal limits. Employers do not receive identifying conversation details." },
            { icon: HeartPulse, title: "Non-clinical care", body: "Trained chaplaincy, not therapy — relational presence and listening." },
            { icon: Users, title: "Chaplain experience", body: "Workplace-trained chaplains skilled in grief, crisis, and corporate context." },
            { icon: Compass, title: "Leadership support", body: "Confidential 1:1 support for executives and managers under pressure." },
            { icon: ShieldCheck, title: "Crisis response", body: "Rapid on-call response for sudden loss, incidents, and workforce events." },
          ].map((c) => (
            <div key={c.title} className="bg-white border border-slate-200 rounded-2xl p-7">
              <c.icon className="w-5 h-5 text-[#4A6FA5]" strokeWidth={1.75} />
              <h3 className="mt-4 font-heading text-lg font-medium text-slate-900">{c.title}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{c.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="py-16 md:py-24 bg-[#F8FAFC]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-3">Our approach</p>
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-slate-900">
            Relational, not transactional. Human, not clinical.
          </h2>
          <div className="mt-6 space-y-5 text-slate-700 leading-relaxed">
            <p>
              CCCHAPS chaplains are trained, background-checked professionals who provide steady, non-clinical presence in the workplace. We're not therapists, and we don't replace the important work of licensed clinicians. What we do is show up consistently — walking the floor, remembering names, listening carefully — so that when something hard happens, the relationship is already there.
            </p>
            <p>
              We serve organizations of every kind — technology, healthcare, hospice, manufacturing, logistics, hospitality, public sector, and nonprofit. Our chaplains care for every employee regardless of religious affiliation, belief, or background. Spiritual care is offered only when an employee asks for it. Otherwise, our work is quiet, human, and confidential.
            </p>
            <p>
              For executives and managers, we offer confidential 1:1 support with no reporting line and no performance implications — a rare kind of space in modern work.
            </p>
          </div>
          <div className="mt-10">
            <Link to="/for-organizations" className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3B597B]">
              Request a Consultation <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
